import { useCallback, useEffect, useState } from "react";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useQuery } from "react-query";
import { getEmailRecipientList } from "api/emailAPI";
import { EmailRecipientResult } from "api/interfaces/emailInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { getAccountTimeFormat } from "utils/timeUtil";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TitleComponentArrayType } from "components/blocks/subheader/SubHeader";
import { TableTemplate } from "components/templates/default/table/TableTemplate";
import Status from "components/blocks/status/Status";
import { isNull } from "lodash";
import { TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  CommonTable,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import { useMenu } from "hook/useMenu";
import { useIsMobile } from "components/MediaQuery";

interface Props {
  titleComponent: string | React.ReactNode | Array<TitleComponentArrayType>;
}

const initialQueryInfo = {
  total: 0,
  pageNum: 0,
  pageLimit: 20,
  keyword: "",
  sortType: "email",
  sortDirection: SORT_DIRECTION.DESC,
};
export default function EmailRecipient(props: Props): JSX.Element {
  const location = useLocation();

  const isMobile = useIsMobile();
  const params = useParams();
  const emailId = params.id as string;
  //const emailId = state.row.emailId;
  const subject = params.subject as string;

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { menuNavigation, queryNavigate } = useMenu({});

  const titleComponent = [
    {
      label: "Sent Emails",
      onClick: () => menuNavigation({ to: "/manager/emails" }),
    },
    {
      label: subject,
    },
  ];
  const [emailRecipientQueryInfo, setEmailRecipientQueryInfo] =
    useState<BasePagination>({
      ...initialQueryInfo,
    });

  const [status, setStatus] = useState({
    totalTitle: "Total",
    totalValue: 0,
  });
  const onHeaderCell = (column: ColumnType<EmailRecipientResult>) => {
    return {
      onClick: () => {
        if (column.key) {
          setEmailRecipientQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };
  const recipientsColumns: TableColumnsType<EmailRecipientResult> = [
    {
      title: "Recipient",
      render: (value, row) => row.username,
    },
    {
      title: "Email Address",
      sorter: true,
      sortOrder: changeSortOrderAntd(emailRecipientQueryInfo, "userEmail"),
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
      key: "userEmail",
      render: (value, row) => row.email,
      width: isMobile ? "50%" : "30%",
    },
    {
      title: "Email Status",
      render: (value, row) => row.emailStatus,
      width: isMobile ? 50 : 100,
    },
    {
      title: "Received",
      render: (value, row) =>
        row.receivedTime !== undefined
          ? getAccountTimeFormat(row.receivedTime, selectedAccount, true)
          : "N/A",
      width: "15%",
      responsive: ["md"],
    },
    {
      title: "Opened",
      render: (value, row) =>
        row.openedTime !== undefined
          ? getAccountTimeFormat(row.openedTime, selectedAccount, true)
          : "N/A",
      width: "15%",
      responsive: ["md"],
    },
  ];

  const [recipients, setRecipients] = useState<EmailRecipientResult[]>([]);

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setEmailRecipientQueryInfo((info) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? 20
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "email"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? SORT_DIRECTION.DESC
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
        };
      });
    } else {
      setEmailRecipientQueryInfo((info) => {
        return {
          ...info,
          pageNum: 0,
          pageLimit: 20,
          keyword: "",
          sortType: "email",
          sortDirection: SORT_DIRECTION.DESC,
        };
      });
    }
  }, [location]);

  const { error, refetch } = useQuery(
    ["emailRecipient", emailRecipientQueryInfo, emailId],
    () =>
      getEmailRecipientList({
        payload: emailRecipientQueryInfo,
        accountId: selectedAccount.accountId,
        emailId: emailId,
      }),

    {
      retry: 4,
      onSuccess: (res: any) => {
        if (res.error !== 0) {
          setRecipients([]);
          setStatus((info) => {
            return {
              ...info,
              totalValue: 0,
            };
          });
          setEmailRecipientQueryInfo({ ...initialQueryInfo });
          return;
        }
        setRecipients(res.result);
        setStatus((info) => {
          return {
            ...info,
            totalValue: res.page.total,
          };
        });
        setEmailRecipientQueryInfo((info) => {
          return { ...info, ...res.page };
        });
      },
      onError: (e: any) => {
        setRecipients([]);
        setStatus((info) => {
          return {
            ...info,
            totalValue: 0,
          };
        });
        setEmailRecipientQueryInfo({ ...initialQueryInfo });
      },
    }
  );

  const onChangePage = useCallback(
    (page: number, totalRows: number) => {
      // console.log(page, totalRows);
      if (page !== emailRecipientQueryInfo.pageNum + 1) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: emailRecipientQueryInfo.pageLimit,
            pageNum: page - 1,
            keyword: emailRecipientQueryInfo.keyword as string,
            sortType: emailRecipientQueryInfo.sortType,
            sortDirection:
              emailRecipientQueryInfo.sortDirection as SORT_DIRECTION,
          },
        });
        // setEmailRecipientQueryInfo((info) => {
        //   return { ...info, pageNum: (page - 1) as number };
        // });
      }
    },
    [
      emailRecipientQueryInfo.keyword,
      emailRecipientQueryInfo.pageLimit,
      emailRecipientQueryInfo.pageNum,
      emailRecipientQueryInfo.sortDirection,
      emailRecipientQueryInfo.sortType,
      location.pathname,
      queryNavigate,
    ]
  );

  const onClickSearch = useCallback(
    (keyword: string) => {
      queryNavigate({
        to: location.pathname,
        queryInfo: {
          pageLimit: emailRecipientQueryInfo.pageLimit,
          pageNum: 0,
          keyword: keyword as string,
          sortType: emailRecipientQueryInfo.sortType,
          sortDirection:
            emailRecipientQueryInfo.sortDirection as SORT_DIRECTION,
        },
      });
      // setEmailRecipientQueryInfo((query) => {
      //   return {
      //     ...query,
      //     keyword,
      //   };
      // });
      // refetch();
    },
    [
      emailRecipientQueryInfo.pageLimit,
      emailRecipientQueryInfo.sortDirection,
      emailRecipientQueryInfo.sortType,
      location.pathname,
      queryNavigate,
    ]
  );

  return (
    <TableTemplate
      titleComponent={titleComponent}
      keyword={emailRecipientQueryInfo.keyword}
      onClickSearch={onClickSearch}
      centerChildren={<Status status={status} />}
    >
      <CommonTable
        columns={recipientsColumns}
        dataSource={recipients}
        queryInfo={emailRecipientQueryInfo}
        onChangePage={onChangePage}
      />
    </TableTemplate>
  );
}
